import React from 'react';
import { Box } from '@mui/material';
import {
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import ChartHeader from './ChartHeader';
import { ChartHeaderProps } from './types';
import ChartCardWrapper from './ChartCardWrapper';

interface PieChartData {
  name: string;
  value: number;
  color?: string;
  [key: string]: string | number | undefined;
}

interface PieChartProps extends ChartHeaderProps {
  data: PieChartData[];
  colors?: string[];
  height?: number;
  graphHeight?: number;
  showLabels?: boolean;
  showLegend?: boolean;
  onDetailsClick?: (data?: any) => void;
  gridExist?: boolean;
}

const RAD = Math.PI / 180;
const shortName = (s = '', max = 12) => (s.length > max ? `${s.slice(0, max)}...` : s);

const renderLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  name,
  value,
}: any) => {
  const r = outerRadius + 45;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  const anchor = x > cx ? 'start' : 'end';
  const pct = `${((Number(percent) || 0) * 100).toFixed(0)}%`;

  return (
    <text
      x={x}
      y={y}
      textAnchor={anchor}
      dominantBaseline="central"
      fontSize={12}
      fill="#556070"
      style={{ pointerEvents: 'auto', cursor: 'pointer' }}
    >
      <title>{`${name}: ${value} (${pct})`}</title>
      {`${shortName(name)} ${pct}`}
    </text>
  );
};

// Leader line from the slice to the label (elbow style)
const renderLabelLine = ({ cx, cy, midAngle, outerRadius }: any) => {
  const r1 = outerRadius + 4;
  const r2 = outerRadius + 40;
  const sx = cx + r1 * Math.cos(-midAngle * RAD);
  const sy = cy + r1 * Math.sin(-midAngle * RAD);
  const mx = cx + r2 * Math.cos(-midAngle * RAD);
  const my = cy + r2 * Math.sin(-midAngle * RAD);
  const ex = mx + (mx > cx ? 8 : -8);
  const ey = my;

  return (
    <path
      d={`M${sx},${sy} L${mx},${my} L${ex},${ey}`}
      stroke="#CCD4DD"
      fill="none"
      strokeWidth={1}
      shapeRendering="geometricPrecision"
    />
  );
};

const PieChart: React.FC<PieChartProps> = ({
  title,
  description,
  data,
  colors = ['#8E79FF', '#F9C07D', '#B794F6', '#6EA8FF', '#63D3B1', '#FF8A65', '#4F86F7', '#6CC5D9', '#8BC34A', '#F06292'],
  height = 340,
  graphHeight = 340,
  showLabels = true,
  showLegend = true,
  details,
  onDetailsClick,
  gridExist = false,
}) => {
  const total = data.reduce((acc, d) => acc + Number(d.value || 0), 0);
  const handlePieClick = (data: any, index: number) => {
    if (onDetailsClick) {
      onDetailsClick(data);
    }
    console.log('Clicked Donut Chart Segment:');
    console.log('Segment Name:', data.name);
    console.log('Value:', data.value);
    console.log('Percentage:', total ? `${((data.value / total) * 100).toFixed(2)}%` : '0%');
    console.log('Index:', index);
    console.log('Full Data:', data);
  };

  return (
    <ChartCardWrapper sx={{ height }}>
      <ChartHeader title={title} description={description} details={details} />
      <Box
        sx={{
          position: 'relative',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
          '& .recharts-pie-sector': {
            outline: 'none !important',
            stroke: 'transparent !important',
            strokeWidth: '0 !important',
          },
        }}
      >
        <ResponsiveContainer width="100%" height={graphHeight}>
          <RechartsPieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              dataKey="value"
              outerRadius={80}
              startAngle={90}
              endAngle={-270}
              minAngle={2}
              isAnimationActive={false}
              labelLine={showLabels ? renderLabelLine : false}
              label={showLabels ? renderLabel : false}
              style={{ cursor: gridExist ? 'pointer' : 'default' }}
              onClick={handlePieClick}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color || colors[index % colors.length]}
                  style={{ outline: 'none', cursor: gridExist ? 'pointer' : 'default' }}
                />
              ))}
            </Pie>
            <Tooltip
              content={(tooltipProps: any) => {
                const { active, payload } = tooltipProps || {};
                if (!active || !payload || !payload.length) return null;
                const entry = payload[0];
                const data = entry.payload as PieChartData;
                const fill = (entry.fill as string) || data.color || '#000';
                const value = Number(data?.value) || 0;
                const pct = total ? `${Math.round((value / total) * 100)}%` : '0%';
                const uniqueId = (data && (data.id || data.unique || data.uid || data.key || data.valueId)) || '';
                const labelLeftTop = 'Name (Unique value)';
                const labelLeftBottom = 'Total (Sum)';

                return (
                  <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 2px 6px rgba(0,0,0,0.12)', padding: 12, border: '1px solid rgba(0,0,0,0.08)', minWidth: 260 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, background: fill, borderRadius: 2 }} />
                        <div style={{ color: '#6b7280', fontSize: 13 }}>{labelLeftTop}</div>
                      </div>
                      <div style={{ color: '#111827', fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{`${data?.name || ''}${uniqueId ? ` (${uniqueId})` : ''}`}</div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ color: '#6b7280', fontSize: 13 }}>{labelLeftBottom}</div>
                      <div style={{ color: '#111827', fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{`${value} (${pct})`}</div>
                    </div>
                  </div>
                );
              }}
            />
            {showLegend && (
              <Legend
                verticalAlign="bottom"
                align="left"
                wrapperStyle={{
                  paddingTop: 20,
                  paddingLeft: 4,
                  paddingRight: 4,
                  paddingBottom: 4,
                  fontSize: 10
                }}
                formatter={(value: string, entry: any) => {
                  const item = data.find(d => d.name === value);
                  const itemValue = item?.value || 0;
                  const pct = total ? Math.round((Number(itemValue) / total) * 100) : 0;
                  return (
                    <span
                      title={`${value}: ${itemValue} (${pct}%)`}
                      style={{
                        verticalAlign: 'middle',
                        lineHeight: '20px',
                        display: 'inline-block'
                      }}
                    >
                      {shortName(value, 7)}
                    </span>
                  );
                }}
                iconType="square"
              />
            )}
          </RechartsPieChart>
        </ResponsiveContainer>
      </Box>
    </ChartCardWrapper>
  );
};

export default PieChart;
